// src/components/ImprovedDocumentsColumn.tsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, ChevronRight, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { DocumentLink } from "@/types"; // Importera typen från types.ts

// Definiera en mer specifik typ för activeProcessData
interface ActiveProcessData {
  title: string;
  hierarchicalNumber?: string;
  description?: string;
  documents: DocumentLink[];
  viktigt?: string;
  detailedDescription?: string;
}

interface DocumentsColumnProps {
  activeProcessData: ActiveProcessData | null | undefined;
}

const ImprovedDocumentsColumn: React.FC<DocumentsColumnProps> = ({ activeProcessData }) => {
  const hasDocuments = activeProcessData && 
                      activeProcessData.documents && 
                      activeProcessData.documents.length > 0;
  
  // Om inget momentdata har valts
  if (!activeProcessData) {
    return (
      <Card className="h-full bg-gray-800/20 border-gray-700/50 backdrop-blur-md">
        <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
          <FileText className="h-8 w-8 mb-3 text-gray-600" />
          <p className="text-sm text-gray-500">Välj ett moment för att se tillhörande dokument</p>
        </CardContent>
      </Card>
    );
  }
  
  // Om momentet är valt men saknar dokument
  if (!hasDocuments) {
    return (
      <Card className="h-full bg-gray-800/20 border-gray-700/50 backdrop-blur-md">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-orange-300" />
            <h3 className="text-lg font-medium text-white">Dokument</h3>
          </div>
          
          <div className="flex flex-col items-center justify-center bg-gray-800/30 rounded-lg p-8 mt-2">
            <FileText className="h-12 w-12 mb-3 text-gray-600" />
            <p className="text-gray-400 mb-2">Inga dokument tillgängliga</p>
            <p className="text-xs text-gray-500 text-center">
              Detta moment har inga kopplade dokument
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Normala fallet med dokument
  return (
    <div className="scroll-to-documents">
      <AnimatePresence mode="wait">
        <motion.div
          key={`docs-${activeProcessData.hierarchicalNumber}`}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 10 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="h-full bg-gray-800/20 border-gray-700/50 backdrop-blur-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-orange-300" />
                <h3 className="text-lg font-medium text-white">Dokument</h3>
                <span className="text-sm text-gray-400 ml-auto">({activeProcessData.documents.length})</span>
              </div>
              
              <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto custom-scrollbar pr-2">
                {activeProcessData.documents.map((doc: DocumentLink, index: number) => {
                  // Extrahera till en variabel för bättre typkontroll
                  const documentHasValidUrl = doc.url && doc.url.trim().startsWith("http");
                  
                  return documentHasValidUrl ? (
                    // Dokument med giltig URL - klickbart
                    <motion.div
                      key={`${doc.title}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="group flex items-center gap-2 text-orange-300 hover:text-orange-400 cursor-pointer bg-gray-800/50 p-3 rounded-lg text-sm hover:bg-gray-700/50 transition-colors w-full"
                      onClick={() => window.open(doc.url, "_blank")}
                    >
                      <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      <span className="text-left flex-1">{doc.title}</span>
                      <ExternalLink className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                    </motion.div>
                  ) : (
                    // Dokument utan URL - inte klickbart
                    <motion.div
                      key={`${doc.title}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="flex items-center gap-2 text-gray-400 bg-gray-800/30 p-3 rounded-lg text-sm border border-gray-700/30"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="text-left flex-1">{doc.title}</span>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ImprovedDocumentsColumn;